import { createRouter, createWebHashHistory } from 'vue-router';
import { createMenuAndRoutes } from './router-utils';

/**
 * @typedef {import('vue-router').NavigationGuard} NavigationGuard
 * @typedef {import('vue-router').RouteRecordRaw} RouteRecordRaw
 * @typedef {import('vue-router').Router} VueRouter
 */

/**
 * @import {RouteMeta, RouteMenu, AppRouter} from "./type.jsdoc.js"
 */

/**
 * @typedef {function(app:AppRouter):void} RouteGuardRegister
 */

/**
 * 应用路由管理器
 */
export class AppRouter {
    static appRouterInstances = new Map();
    /** @type {RouteMenu[]} */
    menus = [];
    /** @type {RouteRecordRaw[]}*/
    routes = [];
    /** @type {RouteGuardRegister[]} */
    guards = [];
    /** @type {Set<function(menus:RouteMenu[])>} */
    menuChangeListeners = new Set();

    /**
     * 构造函数
     * @param {string} app - 应用
     * @param {RouteMeta[]} [stateMetas=[]]  - 静态路由
     * @param {{ sorts:string[], expanded:string[]}} menuConfig - 菜单排序
     * @param {function(app:AppRouter, userId:string,roles:string[]):Promise} [updateRoleRoutes=undefined]- 角色路由更新方法
     * @returns {any}
     */
    constructor (app, stateMetas = [], menuConfig = {}, updateRoleRoutes = undefined) {
        if (AppRouter.appRouterInstances.has(app)) {
            return AppRouter.appRouterInstances.get(app);
        }
        this._isReady = false;
        this._appName = app;
        this._menuConfig = { ...menuConfig };
        this._staticRoutes = [ ...stateMetas ];
        this._updateRoleRoutes = updateRoleRoutes;
        const { menus, routes } = createMenuAndRoutes(app, stateMetas);

        this.menus = menus;
        this.routes = routes;
        this._router = createRouter({
            history: createWebHashHistory(process.env.VUE_APP_BASE_URL),
            routes: this.routes,
        });
        AppRouter.appRouterInstances.set(app, this);
    }

    /**
     * 设置菜单变动监听器
     * @param {function(menus:RouteMenu[])} listener - 菜单变动回调监听器
     * @param {boolean} [immediate = false] - 初始化时是否回调
     */
    setMenuChangeListener (listener, immediate) {
        if (!listener || typeof listener !== 'function') {
            return;
        }
        this.menuChangeListeners.add(listener);
        if (immediate) {
            listener(this.menus);
        }
        return () => {
            if (this.menuChangeListeners.has(listener)) {
                this.menuChangeListeners.delete(listener);
            }
        };
    }

    doMenuChanged () {
        for (const listener of Array.from(this.menuChangeListeners)) {
            listener(this.menus);
        }
    }

    /**
     * @private
     */
    applyRoutes () {
        this._router.clearRoutes();
        for (const route of this.routes) {
            this._router.addRoute(route);
        }
    }

    /**
     * @private
     * @param routeMetas
     * @returns {RouteRecordRaw[]}
     */
    updateRoutes (routeMetas) {
        const { menus, routes } = createMenuAndRoutes(this._appName, routeMetas);
        if (this._menuConfig) {
            const { sorts = [], expanded } = this._menuConfig || {};
            const expandedSet = new Set(expanded || []);
            const sortMaps = new Map();
            for (let i = 0; i < sorts.length; i++) {
                sortMaps.set(sorts[i], i);
            }
            for (let i = 0; i < menus.length; i++) {
                menus[i].expanded = expandedSet.has(menus[i].title);
                if (sortMaps.has(menus[i].title)) {
                    menus[i].order = sortMaps.get(menus[i].title);
                }
                // 子菜单项排序
                if (menus[i].children && typeof Array.isArray(menus[i].children)) {
                    menus[i].children.sort(function (a, b) {
                        return a.order - b.order;
                    });
                }
            }
            menus.sort(function (a, b) {
                return a.order - b.order;
            });
        }
        this.routes = routes;
        this.menus = menus;
        this.doMenuChanged();
        return routes;
    }

    /**
     * 清空路由，并设置为静态路由
     */
    clear () {
        this.updateRoutes(this._staticRoutes);
        this.applyRoutes();
    }

    /**
     * 设置路由
     * @param {RouteMeta[]} routeMetas
     * @returns {RouteRecordRaw[]}
     */
    setRoutes (routeMetas) {
        const routes = this.updateRoutes(routeMetas);
        this.applyRoutes();
        return routes;
    }

    /**
     * 设置路由是否已准备就绪
     * @param {boolean} ready
     */
    setIsReady (ready) {
        this._isReady = ready;
    }

    get isReady () {
        return this._isReady;
    }

    /**
     * 根据角色更新路由
     * @param {string} userId - 用户id
     * @param {string[]} roles  - 用户角色
     */
    async updateRoleRoutes (userId, roles) {
        if (!!this._updateRoleRoutes && typeof this._updateRoleRoutes === 'function') {
            this._isReady = await this._updateRoleRoutes(this, userId, roles);
            return this.isReady;
        }
        return false;
    }

    get router () {
        return this._router;
    }

    /**
     * 使用路由守卫
     * @param {RouteGuardRegister} [registers=[]] 路由守卫注册器列表
     */
    useGuard (...registers) {
        this.guards.push(...registers);
        for (const register of registers) {
            register(this);
        }
    }
}

/**
 * 应用路由
 * @param {string} app - 应用名称
 * @returns {VueRouter}
 */
export const routerByApp = (app) => {
    const inst = new AppRouter(app);
    return inst.router;
};
